import {
  Box,
  Button,
  DialogProps,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import React, { useMemo } from "react";
import BasicModal from "../../../components/modal/BasicModal";
import { useSnackbarHelper } from "../../../components/Snackbar";
import { useFieldArray, useForm, useFormContext } from "react-hook-form";
import Select from "../../../components/inputs/Select";
import BasicDatePicker from "../../../components/inputs/BasicDatePicker";
import NumberField from "../../../components/inputs/NumberField";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import useCases from "../../../../hooks/useCases";
import dayjs from "dayjs";

interface Props extends DialogProps {
  onClose: () => void;
  caseData?: any;
  caseId?: any;
}

const AddPriorPayment = ({ onClose, caseData, caseId, ...props }: Props) => {
  const { addPriorPayment, isAddingPriorPayment } = useCases(caseId);
  const showSnackbar = useSnackbarHelper();
  const initialValues = useMemo(
    () => ({
      priorPayment: [{ type: "", paidDate: null, amount: "" }],
    }),
    []
  );
  const priorPaymentSchema = Yup.object({
    type: Yup.string().required("Required"),
    paidDate: Yup.date().required("Required"),
    amount: Yup.string()
      .required("Required")
      .test("min-amount", "Amount must be at least $1", (value) => {
        const num = parseFloat(value);
        return !isNaN(num) && num >= 1;
      }),
  });

  const validationSchema: any = useMemo(
    () =>
      Yup.object({
        priorPayment: Yup.array().of(priorPaymentSchema).required("Required"),
      }),

    //eslint-disable-next-line
    []
  );
  const formContext = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const handleSubmit = async (values: any) => {
    const { priorPayment } = values;
    let totalPriorPayment = 0;
    totalPriorPayment = priorPayment
      ?.map((payment: any) => Number(payment?.amount) || 0)
      .reduce((sum: any, amount: any) => sum + amount, 0);
    if (totalPriorPayment > caseData?.feeQuoted) {
      showSnackbar("Prior payment cannot exceed the quoted fee.", "error");
      return; // Prevents form submission
    }
    values.feeQuoted = (caseData?.feeQuoted - totalPriorPayment).toString();
    values.priorPayment = priorPayment;
    try {
      const response = await addPriorPayment(values);
      if (response) {
        const message = response?.message || "Success";
        showSnackbar(message, "success");

        onClose();
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "An error occurred";
      showSnackbar(message, "error");
    }
  };
  return (
    <Box>
      <BasicModal
        formContext={formContext}
        onClose={onClose}
        onSubmit={handleSubmit}
        isLoader={isAddingPriorPayment}
        loadingTitle={"Loading..."}
        title="Prior Payment"
        content={<SupportContact />}
        actions={
          <>
            <Button
              color="error"
              variant="outlined"
              onClick={onClose}
              disabled={isAddingPriorPayment}
              sx={{ textTransform: "capitalize" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              sx={{ textTransform: "capitalize" }}
              disabled={isAddingPriorPayment}
              type="submit"
            >
              Save
            </Button>
          </>
        }
        {...props}
      />
    </Box>
  );
};

export default AddPriorPayment;

const SupportContact: React.FC = () => {
  const { control } = useFormContext();

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "priorPayment",
  });

  const RenderEmailsItem = ({ i, arrayLength }: any) => {
    return (
      <Grid container spacing={1} mb={1.5} pt={1}>
        <Grid item xs={3}>
          <Select
            label="Type"
            name={`priorPayment.${i}.type`}
            required
            options={[
              { label: "Check", value: "cheque" },
              { label: "Cash", value: "cash" },
              { label: "Law pay", value: "lawpay" },
            ]}
            fullWidth
          />
        </Grid>
        <Grid item xs={4}>
          <BasicDatePicker
            label="Paid Date"
            name={`priorPayment.${i}.paidDate`}
            required
            maxDate={dayjs(new Date())}
          />
        </Grid>
        <Grid item xs={4}>
          <NumberField
            label="Amount"
            name={`priorPayment.${i}.amount`}
            prefix="$ "
            max={999999999}
            required
            // decimalScale={2}
            // fixedDecimalScale={true}
          />
        </Grid>

        <Grid item xs={1}>
          {i > 0 ? (
            <Tooltip title="Remove">
              <IconButton onClick={() => removeEmail(i)}>
                <RemoveCircleOutlineIcon style={{ color: "red" }} />
              </IconButton>
            </Tooltip>
          ) : i === 0 ? (
            <Tooltip title="Add">
              <IconButton
                onClick={() =>
                  appendEmail({ type: "", paidDate: null, amount: "" })
                }
              >
                <AddCircleOutlineIcon
                  sx={{ color: "green", cursor: "pointer" }}
                />
              </IconButton>
            </Tooltip>
          ) : (
            ""
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        {/* <Divider sx={{ my: 0.5 }} /> */}
      </Grid>
      {emailFields.map((item: any, i, arr) => (
        <RenderEmailsItem key={item.id} i={i} arrayLength={arr.length} />
      ))}

      {/* <Grid item xs={12}>
        <Divider sx={{ my: 0.5 }} />
      </Grid> */}
    </>
  );
};
